/**
 * CurrencyInput — Text input with a "$" prefix and auto-format-to-2-decimals
 * on blur.
 *
 * Two usage patterns are supported via the same component:
 *
 *   1. Parent stores a STRING: pass `value: string` + `onChange(string)`.
 *      The parent receives every keystroke and decides when to parse.
 *      Used by the rodeo wizard dialogs whose local form state is
 *      string-based.
 *
 *   2. Parent stores a NUMBER: same props, but the parent normalizes on each
 *      change (e.g. parseFloat and store 0 if NaN). The component keeps a
 *      local "draft" string so intermediate states like "5." don't round-trip
 *      into "5" and lose the trailing dot. External `value` changes while
 *      NOT focused are honored (e.g. when the parent resets the form).
 *
 * On blur, a valid numeric draft is rewritten to 2 decimals ("5" → "5.00").
 * Invalid/empty drafts are left alone so callers can decide whether to treat
 * them as null.
 */

import * as React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface CurrencyInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'type' | 'inputMode'
  > {
  value: string;
  onChange: (value: string) => void;
  /** Override if you need a different symbol or none at all. */
  symbol?: string;
}

export const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  function CurrencyInput(
    { value, onChange, symbol = '$', className, placeholder = '0.00', onBlur, onFocus, ...rest },
    ref,
  ) {
    // Local draft mirrors the input's visible text so intermediate states
    // like "5." aren't flattened when the parent stores a number and
    // echoes back "5" on the next render.
    const [draft, setDraft] = React.useState<string>(value ?? '');
    const [focused, setFocused] = React.useState(false);

    // Sync FROM parent when not focused (e.g. external reset, initialValues
    // hydration). While focused, the user's typing wins.
    React.useEffect(() => {
      if (focused) return;
      setDraft(value ?? '');
    }, [value, focused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
        setDraft(v);
        onChange(v);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      if (draft !== '') {
        const num = parseFloat(draft);
        if (!Number.isNaN(num)) {
          const formatted = num.toFixed(2);
          if (formatted !== draft) {
            setDraft(formatted);
            onChange(formatted);
          }
        }
      }
      onBlur?.(e);
    };

    return (
      <div className="relative">
        {symbol && (
          <span
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground"
            aria-hidden="true"
          >
            {symbol}
          </span>
        )}
        <Input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={cn(symbol ? 'pl-7' : undefined, className)}
          {...rest}
        />
      </div>
    );
  },
);

export default CurrencyInput;
